import type { IToolConfig, IWatermarkApplyAttrs, IWatermarkAttrs, TApplyBackgroundProp } from 'pages/Batch/types';
import backgroundProcess from 'pages/Batch/utils/process/backgroundProcess';
import removeBgProcess from 'pages/Batch/utils/process/removeBgProcess';
import aiBackgroundProcess from 'pages/Batch/utils/process/aiBackgroundProcess';
import { resizeImage } from 'pages/Batch/utils/process/resizeProcess';
import cropImage from 'pages/Batch/utils/process/cropImage';
import { BATCH_ACTIONS } from 'pages/Batch/configs/index';
import enhanceByIntensity from 'pages/Batch/utils/process/enhanceByIntensity';
import { getWfxProcess } from 'pages/Batch/utils/process/wfxProcess';
import type { WfxProcessAttrs } from 'pages/Batch/utils/process/wfxProcess';
import { getEditorLibProcess } from 'pages/Batch/utils/process/editorLibProcess';
import type { EditorLibProcessAttrs } from 'pages/Batch/utils/process/editorLibProcess';
import { getAdjustProcess } from 'pages/Batch/utils/process/adjustProcess';
import type { AdjustProcessAttrs } from 'pages/Batch/utils/process/adjustProcess';
import { getComposedEnhanceProcess } from 'pages/Batch/utils/process/composedEnhanceProcess';
import type { ComposedEnhanceAttrs } from 'pages/Batch/utils/process/composedEnhanceProcess';

import pluggableApisService from 'services/pluggableApis';

import type { ICropFlip, ICropAttrsBatch, IResizeItemSetting } from 'types/store';

import partial from 'lodash/partial';
import watermarkGenerator from '../utils/watermark/watermarkGenerator';
import { getCachedLogo } from '../utils/watermark/logoCache';

type TProp = {
  width: number;
  height: number;
  usePadding?: boolean;
  bgColor: string | null;
  itemSettings?: Record<string, IResizeItemSetting>;
};
type ApplyEnhanceProp = {
  enhanceMapping: Record<string, string>;
  intensity: number;
  itemIntensities?: Record<string, number>;
};
type CropProp = { attrs: ICropAttrsBatch[]; rotate: number; flip: ICropFlip };

const toolsConfig: { [key in BATCH_ACTIONS]: IToolConfig } = {
  [BATCH_ACTIONS.removeBackground]: {
    getProcess: () => (_: HTMLImageElement, isCancel: () => boolean, itemKey?: string) => removeBgProcess(itemKey as string, isCancel),
  },
  [BATCH_ACTIONS.applyBackground]: {
    getProcess:
      ({ texture, color, objectPosition, ratio, foregroundTransforms, foregroundContentBboxes, itemSettings, textures }: TApplyBackgroundProp) =>
      (image: HTMLImageElement, _isCancel: () => boolean, itemKey?: string) => {
        const snap = itemKey ? itemSettings?.[itemKey] : undefined;
        const foregroundTransform = itemKey ? foregroundTransforms?.[itemKey] : undefined;
        const foregroundContentBbox = itemKey ? foregroundContentBboxes?.[itemKey] : undefined;
        // When a per-item snapshot exists, use ONLY its values — never fall back to the global texture/
        // color, or a deselected item with no image would wrongly inherit the latest applied background.
        // (texture wins over color, matching the single-setting global path.)
        if (snap) {
          const snapTexture = snap.customBgUrl ? textures?.[snap.customBgUrl] : undefined;
          return backgroundProcess(image, {
            texture: snapTexture,
            color: snapTexture ? undefined : snap.color ?? undefined,
            objectPosition: snap.objectPosition,
            ratio: snap.ratio,
            foregroundTransform,
            foregroundContentBbox,
          });
        }
        return backgroundProcess(image, { texture, color, objectPosition, ratio, foregroundTransform, foregroundContentBbox });
      },
    usePending: true,
  },
  [BATCH_ACTIONS.generateAIBackground]: {
    getProcess:
      ({ prompt }: { prompt: string }) =>
      (_: HTMLImageElement, isCancel: () => boolean, itemKey?: string) =>
        aiBackgroundProcess(prompt, itemKey as string, isCancel),
  },
  [BATCH_ACTIONS.resize]: {
    getProcess:
      ({ width, height, usePadding, bgColor, itemSettings }: TProp) =>
      (image: HTMLImageElement, _isCancel: () => boolean, itemKey?: string) => {
        // Per-item snapshot wins so each item resizes to its own size; falls back to the global params.
        // width/height may be undefined (original size) — resizeImage treats falsy as "keep original".
        const s = (itemKey ? itemSettings?.[itemKey] : undefined) ?? { width, height, usePadding, bgColor };
        return resizeImage(image, s.width as number, s.height as number, s.usePadding, s.bgColor ?? bgColor);
      },
  },
  [BATCH_ACTIONS.applyCrop]: {
    getProcess:
      ({ attrs, rotate, flip }: CropProp) =>
      // @ts-ignore
      (image: HTMLImageElement, isCancel: () => boolean, key?: string) =>
        cropImage(image, attrs, rotate, flip, key as string),
  },
  [BATCH_ACTIONS.aiEnhance]: {
    getProcess: () => (image: HTMLImageElement, isCancel: () => boolean, itemKey?: string) => partial(pluggableApisService.enhanceImage.bind(pluggableApisService))(image, itemKey as string, isCancel),
    saveAsEnhanced: true,
  },
  [BATCH_ACTIONS.applyEnhance]: {
    getProcess:
      ({ intensity, enhanceMapping, itemIntensities }: ApplyEnhanceProp) =>
      (image: HTMLImageElement, _isCancel: () => boolean, itemKey?: string) => {
        const itemIntensity = itemKey && itemIntensities?.[itemKey] !== undefined ? itemIntensities[itemKey] : intensity;
        return enhanceByIntensity(image, enhanceMapping, itemIntensity);
      },
  },
  [BATCH_ACTIONS.watermark]: {
    getProcess: (arg?: IWatermarkAttrs | IWatermarkApplyAttrs) => {
      // Apply: render each item full-res from its own snapshot, resolving its logo from the cache by id.
      if (arg && 'itemSettings' in arg) {
        const { itemSettings } = arg;
        return (image: HTMLImageElement, _isCancel: () => boolean, itemKey?: string) => {
          const snap = itemKey ? itemSettings[itemKey] : undefined;
          return watermarkGenerator.generate(snap?.attrs, snap ? getCachedLogo(snap.logoId) : undefined, true)(image);
        };
      }
      // Preview: instance-state render (downscaled), unchanged.
      return watermarkGenerator.generate(arg as IWatermarkAttrs | undefined);
    },
  },
  [BATCH_ACTIONS.wfx]: {
    getProcess: (attrs: WfxProcessAttrs & Partial<EditorLibProcessAttrs>) => {
      if (attrs.editorLibCategoryId) {
        return (image: HTMLImageElement, isCancel: () => boolean) => getEditorLibProcess(attrs as EditorLibProcessAttrs)(image, isCancel);
      }
      return (image: HTMLImageElement, isCancel: () => boolean) => getWfxProcess(attrs)(image, isCancel);
    },
  },
  [BATCH_ACTIONS.applyWfx]: {
    getProcess: () => async (image: HTMLImageElement) => ({ result: image.src }),
    usePending: true,
  },
  [BATCH_ACTIONS.adjust]: {
    getProcess: (attrs: AdjustProcessAttrs) => {
      return (image: HTMLImageElement, isCancel: () => boolean) => getAdjustProcess(attrs)(image, isCancel);
    },
  },
  [BATCH_ACTIONS.applyAdjust]: {
    getProcess: () => async (image: HTMLImageElement) => ({ result: image.src }),
    usePending: true,
  },
  [BATCH_ACTIONS.composedEnhance]: {
    getProcess: (attrs: ComposedEnhanceAttrs) => {
      return (image: HTMLImageElement, isCancel: () => boolean, itemKey?: string) => getComposedEnhanceProcess(attrs)(image, isCancel, itemKey);
    },
    useEnhanced: true,
  },
} as const;

export default toolsConfig;
